import { useEffect, useMemo, useState } from 'react';
import { PieChart, LoadingSpinner } from '@shared/components';
import useAsset from './hooks/useAsset';
import { TitleContainer } from './components';
import { TriangleAlert } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router';
import { useAuthStore } from '@auth/authStore';

function AssetDetailsPage() {
  const [content, setContent] = useState([]);
  const { getData, isLoading, error } = useAsset();
  const { assetId: routeAssetID } = useParams();
  const [searchParams] = useSearchParams();
  const companyID = useAuthStore((state) => state.companyID);
  const selectedSiteID = useAuthStore((state) => state.siteID);
  const selectedAssetID = useAuthStore((state) => state.assetID);
  const siteID = searchParams.get('siteID') ?? selectedSiteID;
  const assetID = routeAssetID ?? selectedAssetID;
  const hasAssetScope = Boolean(companyID && siteID && assetID);

  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      if (!hasAssetScope) return;
      const response = await getData(companyID, siteID, assetID);
      if (isMounted) setContent(response);
    }

    loadData();

    return () => {
      isMounted = false;
    };
  }, [assetID, companyID, getData, hasAssetScope, siteID]);

  const pieChartData = useMemo(() => {
    if (!content?.DamageInfo) return [];

    const totals = {};

    Object.values(content.DamageInfo).forEach(list => {
      list.forEach(item => {
        if (!totals[item.name]) {
          totals[item.name] = 0;
        }
        totals[item.name] += item.count;
      });
    });
    return Object.entries(totals).map(([name, totalCount]) => ({
      name,
      totalCount,
    }));
  }, [content]);

  if (isLoading) {
    return (
      <div className='basicWrapper'>
        <span>Loading...</span>
        <LoadingSpinner color="black" />
      </div>
    );
  }

  if (error) {
    return (
      <div className='basicWrapper'>
        <TriangleAlert color="var(--color-deep)" />
        <span>Error: {error}</span>
      </div>
    );
  }

  if (!hasAssetScope) {
    return (
      <div className='basicWrapper'>
        <TriangleAlert color="var(--color-deep)" />
        <span>Choose an asset from the Assets page to view its details.</span>
      </div>
    );
  }

  return (
    <div className='basicWrapper'>
      <TitleContainer
        assetName={content.Asset ?? assetID}
        assetType="Type"
        assetLocation={siteID}
      />

      <PieChart
        data={pieChartData}
        title="Asset Total Damages"
        dataKey="totalCount"
        nameKey="name"
        innerRadius={40}
      />
    </div >
  );
}

export default AssetDetailsPage;
